import React, { useState } from 'react';

/**
 * "Are you REALLY sure?" modal for archiving + deleting a shop.
 * Requires the master admin to type the shop's subdomain verbatim
 * before the button becomes clickable — same pattern GitHub uses for
 * repo deletion, and enough friction to survive a slip of the mouse.
 */
export const DeleteShopModal = ({ shop, linkedAccountsHint, onClose, onDeleted, callAuth }) => {
  const [confirmText, setConfirmText] = useState('');
  const [alsoDelete, setAlsoDelete] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const matches = confirmText.trim().toLowerCase() === (shop.subdomain || '').toLowerCase();

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const result = await callAuth('/api/auth/delete-shop', {
        shop_id: shop.id,
        confirm_subdomain: confirmText.trim().toLowerCase(),
        delete_linked_accounts: alsoDelete,
      });
      onDeleted(result);
    } catch (err) {
      setError(err.message || 'Delete failed');
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="delete-shop-modal">
        <h3 style={{ color: 'var(--red)' }}>Delete {shop.name}?</h3>
        <div className="modal-sub">
          The shop and all its data (barbers, services, bookings, customers) will be permanently removed.
          A JSON archive is uploaded to Supabase Storage <b>before</b> deletion — customer PII is anonymised via short hashes so reimport preserves referential integrity.
        </div>
        {error && <div className="modal-error">{error}</div>}
        <div className="field">
          <label>Type the subdomain to confirm: <b>{shop.subdomain}</b></label>
          <input
            className="inp"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={shop.subdomain}
            autoFocus
            data-testid="delete-shop-confirm-input"
          />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={alsoDelete}
              onChange={(e) => setAlsoDelete(e.target.checked)}
              data-testid="delete-shop-linked-checkbox"
            />
            <span style={{ fontSize: 'var(--fs-base)' }}>
              Also delete the {linkedAccountsHint} linked login account{linkedAccountsHint === 1 ? '' : 's'} (owner + barbers with logins). Uncheck to leave the auth users in place.
            </span>
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn s" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="submit"
            className="btn p"
            disabled={!matches || saving}
            style={{ background: matches && !saving ? 'var(--red)' : undefined, borderColor: matches && !saving ? 'var(--red)' : undefined }}
            data-testid="delete-shop-confirm-btn"
          >
            {saving ? 'Archiving & deleting…' : 'Archive & delete'}
          </button>
        </div>
      </form>
    </div>
  );
};

/**
 * Post-delete receipt. Confirms the archive was uploaded, shows the
 * signed download URL (7-day validity) and a count-of-rows-removed
 * summary so the master admin has proof-of-work for their records.
 */
export const DeleteResultModal = ({ result, onClose }) => {
  const c = result?.counts || {};
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="delete-shop-result-modal">
        <h3>Shop archived &amp; deleted</h3>
        <div className="modal-sub">
          Archive saved to Supabase Storage bucket <b>{result.archive_bucket}</b>. Signed URL is valid for 7 days.
        </div>
        <div className="field">
          <label>Archive path</label>
          <input className="inp mono" readOnly value={result.archive_path} data-testid="delete-shop-archive-path" />
        </div>
        {result.archive_url && (
          <div className="field">
            <label>Signed download URL</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="inp mono" readOnly value={result.archive_url} style={{ fontSize: 'var(--fs-sm)' }} />
              <a className="btn s" href={result.archive_url} target="_blank" rel="noreferrer" data-testid="delete-shop-archive-download">Download</a>
            </div>
          </div>
        )}
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Removed</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 'var(--fs-sm)' }}>
            <div>Barbers: <b>{c.barbers ?? 0}</b></div>
            <div>Barber hours: <b>{c.barber_hours ?? 0}</b></div>
            <div>Services: <b>{c.services ?? 0}</b></div>
            <div>Customers: <b>{c.customers ?? 0}</b></div>
            <div>Bookings: <b>{c.bookings ?? 0}</b></div>
            <div>User profiles: <b>{c.user_profiles ?? 0}</b></div>
            <div>Auth users found: <b>{c.linked_auth_users ?? 0}</b></div>
            <div>Auth users deleted: <b>{c.deleted_auth_users ?? 0}</b></div>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn p" onClick={onClose} data-testid="delete-shop-result-done">Back to shops</button>
        </div>
      </div>
    </div>
  );
};
